import { useEffect, useId, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import type { JobRow, JobSummary } from "@gwrctl/api-client";
import { client } from "../lib/client.js";
import { humanizeError } from "../lib/errors.ts";
import { jobTitle } from "../lib/labels.ts";
import { useToast } from "../lib/toast.tsx";
import { AppIcon } from "../components/AppIcon.tsx";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import { Banner, Button, Card, Dot, Empty, ErrorState, RowLink, StatusPill } from "../components/Ui.tsx";
import { JobLauncher } from "../components/JobLauncher.tsx";
import { RunPanel } from "../components/RunPanel.tsx";

/**
 * Mağaza sayfası: Play mağaza görselleri ve metinleri.
 *
 * Görseller ve listing.json PC'de durur; konsol onlara yalnızca iş üzerinden ulaşır.
 * `store.overview` durumu (ve istenen dilin küçük önizlemelerini) getirir,
 * `store.listing.write` metni kaydeder. Üretme / önizleme / yükleme uzun işler olduğu
 * için mevcut iş diyaloğuyla başlatılır ve bu sayfada, üstte canlı log'la izlenir —
 * iş sayfasına gidince geri tuşu mağaza sayfasını kaybettiriyordu.
 *
 * Her okuma bir agent işi (2 dakikaya kadar sürebilir). Bu yüzden diller ve metinler
 * uygulama başına BİR kez okunur; dil değiştirmek yalnızca o dilin önizlemelerini
 * ister ve o da dil başına önbellekte kalır.
 */

interface ListingText {
  title: string;
  shortDescription: string;
  fullDescription: string;
}

interface Overview {
  app: string;
  sourceLanguage: string;
  limits: Record<keyof ListingText, number>;
  languages: {
    lang: string;
    play: string;
    phone: number;
    tablet: number;
    feature: boolean;
    generatedAt: string | null;
    listing: ListingText | null;
  }[];
  play: { ok: boolean; error?: string; listings: (ListingText & { language: string })[] };
  selected: string | null;
  thumbs: { name: string; kind: "phone" | "tablet" | "feature"; data: string }[];
}

const FIELDS: { key: keyof ListingText; label: string; rows: number }[] = [
  { key: "title", label: "Uygulama adı", rows: 1 },
  { key: "shortDescription", label: "Kısa açıklama", rows: 2 },
  { key: "fullDescription", label: "Uzun açıklama", rows: 14 },
];

const STORE_JOBS = [
  { id: "store.assets.generate", label: "Görselleri üret" },
  { id: "store.assets.preview", label: "Önizle" },
  { id: "store.assets.upload", label: "Play'e yükle" },
] as const;

const len = (s: string): number => [...s].length;
const DONE = new Set(["succeeded", "failed", "canceled", "expired"]);
// Agent okumaları pahalı; kullanıcı "Yenile" demedikçe yeniden çalıştırılmaz.
const STALE = 10 * 60_000;

/** Kısa agent işini başlatır ve bitmesini bekler (sayfa içi okuma/yazma için). */
async function runAgentJob<T>(type: string, app: string, params: Record<string, unknown>): Promise<T> {
  const created = await client.createJob({ type, app, params: { app, ...params } });
  if (created.queuedOnly) {
    throw new Error("PC agent bağlı değil. Bilgisayar açıkken ve agent çalışırken tekrar dene.");
  }
  const started = Date.now();
  for (;;) {
    const { job } = await client.job(created.job.id);
    if (DONE.has(job.status)) {
      if (job.status !== "succeeded") throw new Error(job.error ?? "İş başarısız oldu.");
      return job.result as T;
    }
    if (Date.now() - started > 120_000) throw new Error("İş 2 dakikada bitmedi; iş kaydından takip et.");
    await new Promise((r) => setTimeout(r, 1000));
  }
}

export function StorePage(): ReactNode {
  const { appId = "" } = useParams();
  const queryClient = useQueryClient();
  const [pickedLang, setPickedLang] = useState<string | null>(null);
  const [launching, setLaunching] = useState<JobSummary | null>(null);
  // Açık iş paneli UYGULAMA BAŞINA: MoviePort'ta yükleme sürerken Raadiocast'e geçip
  // dönünce panel kaybolmasın, Raadiocast'in düğmeleri de MoviePort'un işine kilitlenmesin.
  // null = kullanıcı paneli kapattı (iş sürse de yeniden açılmaz).
  const [runs, setRuns] = useState<Record<string, { id: string; type: string } | null>>({});
  // Kaydedilmemiş metinler dil başına burada durur: dil değiştirince kaybolmasın.
  const [drafts, setDrafts] = useState<Record<string, ListingText>>({});

  // Başka uygulamanın mağazasına geçince eski dil/taslak taşınmasın.
  useEffect(() => {
    setPickedLang(null);
    setDrafts({});
  }, [appId]);

  const dirtyCount = Object.keys(drafts).length;
  useEffect(() => {
    if (!dirtyCount) return;
    const warn = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirtyCount]);

  const appQuery = useQuery({
    queryKey: ["app", appId],
    queryFn: () => client.app(appId),
    enabled: Boolean(appId),
  });
  const appData = appQuery.data;

  // Diller + metinler: uygulama başına tek okuma (dil parametresiz).
  const overview = useQuery({
    queryKey: ["store-overview", appId],
    queryFn: () => runAgentJob<Overview>("store.overview", appId, {}),
    enabled: Boolean(appId),
    staleTime: STALE,
    retry: false,
  });
  const data = overview.data;

  const selected =
    pickedLang ??
    data?.selected ??
    data?.languages.find((l) => l.lang === data.sourceLanguage)?.lang ??
    data?.languages[0]?.lang ??
    null;

  // Önizlemeler dil başına ayrı ve önbellekte; aynı dile tekrar tıklamak yeni iş başlatmaz
  // (aynı anahtar için süren sorgu paylaşılır).
  const thumbs = useQuery({
    queryKey: ["store-thumbs", appId, selected],
    queryFn: async () =>
      (await runAgentJob<Overview>("store.overview", appId, { lang: selected })).thumbs,
    enabled: Boolean(appId && data && selected && data.selected !== selected),
    staleTime: STALE,
    gcTime: 30 * 60_000,
    retry: false,
  });
  const thumbList = data && data.selected === selected ? data.thumbs : (thumbs.data ?? []);

  const jobs = useQuery({
    queryKey: ["jobs", appId],
    queryFn: () => client.jobs({ app: appId, limit: 60 }),
    enabled: Boolean(appId),
    refetchInterval: 10_000,
  });

  const storeJobs = (jobs.data ?? []).filter((j) => j.type.startsWith("store.assets."));
  // Düğmeleri kilitleyen şey açık panel değil, bu uygulamada GERÇEKTEN süren iş: biten işin
  // paneli açık kalsa da yeni iş başlatılabilir. Sayfa yenilense de süren iş görünür.
  const activeJob = storeJobs.find((j) => !DONE.has(j.status));
  const running = appId in runs ? (runs[appId] ?? null) : activeJob ? { id: activeJob.id, type: activeJob.type } : null;
  const busy = Boolean(activeJob);
  const jobSummary = (id: string): JobSummary | undefined => appData?.jobs.find((j) => j.id === id);

  const lang = data?.languages.find((l) => l.lang === selected);
  const playText = lang ? data?.play.listings.find((l) => l.language === lang.play) : undefined;
  const draftKey = (play: string): string => `${appId}:${play}`;

  const refreshAll = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["store-overview", appId] });
    void queryClient.invalidateQueries({ queryKey: ["store-thumbs", appId] });
  };

  return (
    <>
      <PageHeader
        kicker={appData?.app.displayName ?? appId}
        title={
          <span className="flex items-center gap-3">
            <AppIcon id={appId} name={appData?.app.displayName} size={30} />
            Mağaza
          </span>
        }
        actions={
          <>
            <Link to={`/apps/${appId}`} className="btn btn-ghost">
              Uygulama sayfası
            </Link>
            <Button variant="ghost" loading={overview.isFetching} onClick={refreshAll}>
              {overview.isFetching ? "Okunuyor…" : "Yenile"}
            </Button>
            {STORE_JOBS.map(({ id, label }) => {
              const summary = jobSummary(id);
              return (
                <Button
                  key={id}
                  variant={id === "store.assets.upload" ? "primary" : "secondary"}
                  disabled={!summary || busy}
                  title={busy ? "Önce bu uygulamada süren işin bitmesini bekle" : undefined}
                  onClick={() => summary && setLaunching(summary)}
                >
                  {label}
                </Button>
              );
            })}
          </>
        }
      />

      <PageBody>
        {running && (
          <RunPanel
            jobId={running.id}
            onClose={() => {
              // Üretim görselleri, yükleme Play'deki metinleri değiştirir; kapanınca taze durumu oku.
              if (running.type === "store.assets.generate" || running.type === "store.assets.upload") refreshAll();
              setRuns((prev) => ({ ...prev, [appId]: null }));
            }}
          />
        )}

        {appQuery.isError && (
          <Card>
            <ErrorState compact error={appQuery.error} onRetry={() => void appQuery.refetch()} />
          </Card>
        )}
        {overview.isError && (
          <Banner
            tone="danger"
            kicker="okunamadı"
            action={
              <Button size="sm" onClick={() => void overview.refetch()}>
                Tekrar dene
              </Button>
            }
          >
            {humanizeError(overview.error, "Mağaza durumu okunamadı")}
          </Banner>
        )}
        {data && !data.play.ok && (
          <Banner kicker="Play">
            Play'deki metinler okunamadı: {data.play.error ?? "servis hesabı tanımlı değil"}
          </Banner>
        )}
        {dirtyCount > 0 && (
          <Banner tone="info" kicker="taslak">
            {dirtyCount} dilde kaydedilmemiş metin var. Dil değiştirince korunur; sayfadan
            ayrılmadan önce kaydet.
          </Banner>
        )}

        <div className="grid items-start gap-4 lg:grid-cols-[minmax(220px,260px)_minmax(0,1fr)]">
          <Card title="Diller" hint={data ? `${data.languages.length} dil` : undefined} flush>
            {overview.isError ? (
              <Empty>Diller okunamadı</Empty>
            ) : !data ? (
              <Empty>PC'den okunuyor… (en fazla 2 dk)</Empty>
            ) : !data.languages.length ? (
              <Empty>Mağaza projesinde dil tanımlı değil</Empty>
            ) : (
              data.languages.map((l) => {
                const inPlay = data.play.listings.some((p) => p.language === l.play);
                const active = l.lang === selected;
                const dirty = Boolean(drafts[draftKey(l.play)]);
                return (
                  <button
                    key={l.lang}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setPickedLang(l.lang)}
                    className={`row row-hover gap-2.5 ${active ? "bg-neutral-900" : ""}`}
                    style={{ boxShadow: active ? "inset 2px 0 0 var(--color-accent)" : undefined }}
                  >
                    <Dot tone={inPlay ? "ok" : "idle"} />
                    <span className="mono min-w-[52px] text-xs text-neutral-100">{l.play}</span>
                    <span className="text-2xs text-neutral-500">
                      {l.phone ? `${l.phone} tel · ${l.tablet} tab` : "görsel yok"}
                    </span>
                    <span className="ml-auto text-2xs whitespace-nowrap text-neutral-600">
                      {dirty ? (
                        <span className="text-warn">kaydedilmedi</span>
                      ) : inPlay ? (
                        "Play'de"
                      ) : (
                        "yeni"
                      )}
                    </span>
                  </button>
                );
              })
            )}
          </Card>

          <div className="grid min-w-0 gap-4">
            {lang && data ? (
              <>
                <Thumbnails
                  lang={lang}
                  thumbs={thumbList}
                  loading={data.selected !== selected && thumbs.isFetching}
                  error={data.selected !== selected && thumbs.isError ? thumbs.error : null}
                  onRetry={() => void thumbs.refetch()}
                />
                {/* Anahtar yalnızca dil: kaydedince (başlık değişince) yeniden kurulup
                    "Kaydedildi" mesajını silmesin. */}
                <ListingEditor
                  key={`${appId}-${lang.play}`}
                  appId={appId}
                  play={lang.play}
                  limits={data.limits}
                  saved={lang.listing}
                  live={playText ?? null}
                  draft={drafts[draftKey(lang.play)] ?? null}
                  onDraft={(text) =>
                    setDrafts((prev) => {
                      const next = { ...prev };
                      if (text) next[draftKey(lang.play)] = text;
                      else delete next[draftKey(lang.play)];
                      return next;
                    })
                  }
                  onSaved={(text) =>
                    // Yeniden okumak 2 dakikalık bir iş; kaydedilen metni önbelleğe yazmak yeterli.
                    queryClient.setQueryData<Overview>(["store-overview", appId], (old) =>
                      old
                        ? {
                            ...old,
                            languages: old.languages.map((l) =>
                              l.play === lang.play ? { ...l, listing: text } : l,
                            ),
                          }
                        : old,
                    )
                  }
                />
              </>
            ) : (
              <Card>
                <Empty>
                  {overview.isError
                    ? "Durum okunamadı"
                    : overview.isLoading
                      ? "Diller PC'den okunuyor…"
                      : "Bir dil seç"}
                </Empty>
              </Card>
            )}

            <Card title="Son mağaza işleri" flush>
              {jobs.isError ? (
                <ErrorState compact error={jobs.error} onRetry={() => void jobs.refetch()} />
              ) : jobs.isLoading ? (
                <Empty>Yükleniyor…</Empty>
              ) : storeJobs.length ? (
                storeJobs.slice(0, 8).map((job: JobRow) => (
                  <RowLink key={job.id} to={`/jobs/${job.id}`} className="flex-wrap">
                    <StatusPill status={job.status} />
                    <span className="text-sm text-neutral-300">{jobTitle(job.type)}</span>
                    {job.step && job.status === "running" && (
                      <span className="text-2xs text-neutral-500">{job.step}</span>
                    )}
                    <span className="mono ml-auto text-2xs text-neutral-600">
                      {new Date(job.createdAt).toLocaleString("tr-TR")}
                    </span>
                  </RowLink>
                ))
              ) : (
                <Empty>Henüz mağaza işi çalıştırılmadı</Empty>
              )}
            </Card>
          </div>
        </div>

        {launching && appData && (
          <JobLauncher
            job={launching}
            appId={appId}
            channels={appData.app.ota?.channels ?? ["production"]}
            buildScript={appData.app.build?.script}
            onLaunched={(jobId) => {
              setRuns((prev) => ({ ...prev, [appId]: { id: jobId, type: launching.id } }));
              void queryClient.invalidateQueries({ queryKey: ["jobs", appId] });
            }}
            onClose={() => setLaunching(null)}
          />
        )}
      </PageBody>
    </>
  );
}

function Thumbnails({
  lang,
  thumbs,
  loading,
  error,
  onRetry,
}: {
  lang: Overview["languages"][number];
  thumbs: Overview["thumbs"];
  loading: boolean;
  error: unknown;
  onRetry: () => void;
}): ReactNode {
  const groups: { kind: Overview["thumbs"][number]["kind"]; label: string; height: number }[] = [
    { kind: "phone", label: "Telefon", height: 220 },
    { kind: "tablet", label: "Tablet", height: 124 },
    { kind: "feature", label: "Öne çıkan grafik", height: 108 },
  ];
  return (
    <Card
      title={`Görseller · ${lang.play}`}
      hint={lang.generatedAt ? `üretildi: ${new Date(lang.generatedAt).toLocaleString("tr-TR")}` : "henüz üretilmedi"}
    >
      {error ? (
        <ErrorState compact error={error} onRetry={onRetry} />
      ) : thumbs.length ? (
        <div className="grid gap-3">
          {groups.map((g) => {
            const items = thumbs.filter((t) => t.kind === g.kind);
            if (!items.length) return null;
            return (
              <div key={g.kind} className="grid min-w-0 gap-1.5">
                <span className="kicker">{g.label}</span>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {items.map((t) => (
                    <img
                      key={t.name}
                      src={`data:image/jpeg;base64,${t.data}`}
                      alt={`${g.label} · ${t.name}`}
                      title={t.name}
                      style={{ height: g.height }}
                      className="shrink-0 rounded-[6px] border border-neutral-900"
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Empty>
          {loading
            ? "Bu dilin önizlemeleri PC'den geliyor… (en fazla 2 dk; başka dile geçebilirsin, iş sürer)"
            : lang.phone
              ? "Önizleme yok. Görselleri yeniden üret (önizlemeler üretimle birlikte oluşur)."
              : "Bu dil için görsel yok. “Görselleri üret” ile oluştur."}
        </Empty>
      )}
    </Card>
  );
}

function ListingEditor({
  appId,
  play,
  limits,
  saved,
  live,
  draft,
  onDraft,
  onSaved,
}: {
  appId: string;
  play: string;
  limits: Record<keyof ListingText, number>;
  saved: ListingText | null;
  live: ListingText | null;
  /** Kaydedilmemiş metin (üst bileşende tutulur ki dil değişince kaybolmasın). */
  draft: ListingText | null;
  onDraft: (text: ListingText | null) => void;
  onSaved: (text: ListingText) => void;
}): ReactNode {
  const toast = useToast();
  const idBase = useId();
  const initial = saved ?? live ?? { title: "", shortDescription: "", fullDescription: "" };
  const text = draft ?? initial;
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "ok" | "error"; text: string } | null>(null);

  const setText = (next: ListingText): void => {
    setMessage(null);
    // Kayıtlıyla aynıya dönünce taslak düşer; "kaydedilmedi" işareti yanlış kalmasın.
    onDraft(FIELDS.some((f) => next[f.key] !== initial[f.key]) ? next : null);
  };

  const dirty = draft !== null;
  const overLimit = FIELDS.some((f) => len(text[f.key]) > limits[f.key]);
  const empty = FIELDS.some((f) => !text[f.key].trim());
  const differsFromPlay = useMemo(
    () => (live ? FIELDS.some((f) => live[f.key].trim() !== text[f.key].trim()) : true),
    [live, text],
  );

  async function save(): Promise<void> {
    const toSave = text;
    setBusy(true);
    setMessage(null);
    try {
      await runAgentJob("store.listing.write", appId, { play, ...toSave });
      onSaved(toSave);
      onDraft(null);
      setMessage({ tone: "ok", text: "Kaydedildi. Play'e göndermek için “Play'e yükle” (metinleri eşitle açık)." });
    } catch (err) {
      const msg = humanizeError(err, "Kaydedilemedi");
      setMessage({ tone: "error", text: msg });
      toast({ tone: "danger", title: `${play} metni kaydedilemedi`, body: msg });
    } finally {
      setBusy(false);
    }
  }

  const status = !live
    ? { tone: "idle" as const, label: "Play'de bu dilde sayfa yok — yüklemede açılır" }
    : differsFromPlay
      ? { tone: "warn" as const, label: "Play'deki metinden farklı — yüklemede güncellenir" }
      : { tone: "ok" as const, label: "Play'deki metinle aynı" };

  return (
    <Card
      title={`Mağaza metni · ${play}`}
      hint={
        <span className="inline-flex items-center gap-1.5">
          <Dot tone={status.tone} />
          {status.label}
        </span>
      }
      action={
        <>
          {dirty && (
            <Button variant="ghost" onClick={() => setText(initial)} disabled={busy}>
              Geri al
            </Button>
          )}
          <Button variant="primary" onClick={() => void save()} loading={busy} disabled={!dirty || overLimit || empty}>
            {busy ? "Kaydediliyor…" : "Kaydet"}
          </Button>
        </>
      }
    >
      <div className="grid gap-3.5">
        {FIELDS.map((f) => {
          const n = len(text[f.key]);
          const over = n > limits[f.key];
          const id = `${idBase}-${f.key}`;
          return (
            <div key={f.key} className="grid gap-1.5">
              <div className="flex items-baseline">
                <label htmlFor={id} className="label mb-0">
                  {f.label}
                </label>
                <span
                  className="mono ml-auto text-2xs"
                  style={{ color: over ? "var(--color-danger)" : n > limits[f.key] * 0.9 ? "var(--color-warn)" : "var(--color-neutral-600)" }}
                >
                  {n}/{limits[f.key]}
                </span>
              </div>
              {f.rows === 1 ? (
                <input id={id} className="input" readOnly={busy} value={text[f.key]} onChange={(e) => setText({ ...text, [f.key]: e.target.value })} />
              ) : (
                <textarea
                  id={id}
                  className="input min-h-0 leading-[1.5]"
                  readOnly={busy}
                  rows={f.rows}
                  value={text[f.key]}
                  onChange={(e) => setText({ ...text, [f.key]: e.target.value })}
                />
              )}
            </div>
          );
        })}
        {!saved && live && (
          <span className="text-2xs text-neutral-600">
            Bu dilin metni Play'den okundu; kaydedersen bundan sonra buradan yönetilir.
          </span>
        )}
        {busy && (
          <span className="text-xs text-neutral-500">PC'ye yazılıyor… (en fazla 2 dk)</span>
        )}
        {message && (
          <span
            role={message.tone === "error" ? "alert" : "status"}
            className="text-xs"
            style={{ color: message.tone === "ok" ? "var(--color-ok)" : "var(--color-danger)" }}
          >
            {message.text}
          </span>
        )}
      </div>
    </Card>
  );
}
